import React, { useEffect, useMemo, useState } from "react";
import {
  getLandingLeads,
  getLandingLead,
  deleteLandingLead
} from "../services/landingLeadsApi";
import Layout from "../components/Layout";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "";
  }
}

export default function LandingLeads() {
  const [page, setPage] = useState(1);
  const limit = 10;

  const [rows, setRows] = useState([]);
  const [pg, setPg] = useState({ page: 1, limit, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // preview drawer
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [active, setActive] = useState(null);
  const [loadingOne, setLoadingOne] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await getLandingLeads({ page, limit });
      setRows(data.data || []);
      setPg(data.pagination || { page, limit, total: 0, totalPages: 1 });
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load landing leads");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [page]);

  async function openPreview(id) {
    setOpen(true);
    setActiveId(id);
    setActive(null);
    setLoadingOne(true);

    try {
      const doc = await getLandingLead(id);
      setActive(doc);
    } catch (e) {
      setActive({ _error: e?.response?.data?.message || "Failed to load lead" });
    } finally {
      setLoadingOne(false);
    }
  }

  function closePreview() {
    setOpen(false);
    setActiveId("");
    setActive(null);
  }

  async function onDelete(id) {
    const ok = window.confirm("Delete this lead? This cannot be undone.");
    if (!ok) return;

    try {
      await deleteLandingLead(id);

      const remaining = rows.length - 1;
      if (remaining <= 0 && page > 1) setPage((p) => p - 1);
      else load();

      if (activeId === id) closePreview();
    } catch (e) {
      alert(e?.response?.data?.message || "Delete failed");
    }
  }

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows]);

  return (
    <Layout>
      <div className="main-card">
        <div className="topbar">
          <div>
            <div className="brand">
              <span className="brand-dot" />
              Landing Leads
            </div>
            <div className="small">Leads from landing page forms</div>
          </div>
        </div>

        <div style={{ padding: 18 }}>
          {err ? (
            <div className="card" style={{ padding: 14 }}>
              <b style={{ color: "var(--danger)" }}>Error:</b> {err}
            </div>
          ) : null}

          <div className="card" style={{ overflow: "hidden" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>WhatsApp</th>
                  <th>Pin</th>
                  <th>Received</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="muted">Loading...</td>
                  </tr>
                ) : null}

                {empty ? (
                  <tr>
                    <td colSpan="6" className="muted">No leads found.</td>
                  </tr>
                ) : null}

                {!loading &&
                  rows.map((r) => (
                    <tr
                      key={r._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => openPreview(r._id)}
                    >
                      <td><b>{r.fullName}</b></td>
                      <td><span className="badge">{r.type}</span></td>
                      <td>{r.whatsapp || "-"}</td>
                      <td>{r.pinCode || "-"}</td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(r._id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
            <div className="small">Total: <b>{pg.total}</b></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-ghost"
                disabled={pg.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="badge">
                Page {pg.page} / {pg.totalPages || 1}
              </span>
              <button
                className="btn btn-ghost"
                disabled={pg.page >= pg.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* PREVIEW DRAWER */}
        {open ? (
          <div style={styles.overlay} onClick={closePreview}>
            <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
              <div style={styles.drawerHead}>
                <div>
                  <div style={{ fontWeight: 900 }}>Landing Lead Preview</div>
                  <div className="small">{fmtDate(active?.createdAt)}</div>
                </div>
                <button className="btn btn-ghost" onClick={closePreview}>Close</button>
              </div>

              <div style={{ padding: 16 }}>
                {loadingOne ? (
                  <div className="muted">Loading details...</div>
                ) : active?._error ? (
                  <div className="card">{active._error}</div>
                ) : active ? (
                  <>
                    <div className="card" style={{ padding: 14, marginBottom: 12 }}>
                      {Object.entries(active).map(([k, v]) =>
                        ["_id", "__v"].includes(k) || !v ? null : (
                          <div key={k} style={{ marginBottom: 6 }}>
                            <span className="muted">{k}:</span>{" "}
                            <b>{String(v)}</b>
                          </div>
                        )
                      )}
                    </div>

                    <button
                      className="btn btn-danger"
                      onClick={() => onDelete(active._id)}
                    >
                      Delete Lead
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.55)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 9999
  },
  drawer: {
    width: "min(520px, 100%)",
    height: "100%",
    background: "#fff",
    borderLeft: "1px solid var(--border)",
    boxShadow: "0 20px 50px rgba(2,6,23,0.35)"
  },
  drawerHead: {
    padding: 14,
    borderBottom: "1px solid var(--border)",
    display: "flex",
    justifyContent: "space-between",
    background: "linear-gradient(180deg, rgba(16,31,157,0.06), transparent 70%)"
  }
};
